import type { ViewFacilityDetail } from '../../types/viewFacilityTypes';
import { FACILITY_LABELS } from '../../constants/facilityLabels';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(value: string | null | undefined): string {
  return value?.trim() || '—';
}

function formatPhone(
  countryCode: string | null | undefined,
  phone: string | null | undefined
): string {
  if (!phone) {
    return '—';
  }
  return `${countryCode ?? '+48'} ${phone}`;
}

function formatNip(raw: string | null | undefined): string {
  if (!raw) {
    return '—';
  }
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) {
    return '—';
  }
  if (digits.length > 8) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  if (digits.length > 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return digits;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  return new Date(iso)
    .toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\./g, '/')
    .split('/')
    .reverse()
    .join('/');
}

// ─── InfoField ────────────────────────────────────────────────────────────────

interface InfoFieldProps {
  label: string;
  value: string;
  testId?: string;
}

const InfoField = ({ label, value, testId }: InfoFieldProps) => (
  <div className="flex min-w-0 flex-col gap-2">
    <h2
      className="text-bodySmall font-normal text-on-surface-variant"
      data-testid={testId ?? label}
    >
      {label}
    </h2>
    <p
      className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10"
      data-testid={testId ? `${testId}-value` : `${label}-value`}
    >
      {value}
    </p>
  </div>
);

// ─── ViewFacilityBasicInfo ────────────────────────────────────────────────────

interface ViewFacilityBasicInfoProps {
  facility: ViewFacilityDetail | null;
}

const ViewFacilityBasicInfo = ({ facility }: ViewFacilityBasicInfoProps) => {
  return (
    <div className="flex flex-col gap-6">
      {/* Podstawowe dane */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p className="text-titleDefault font-medium text-on-surface-variant">
          {FACILITY_LABELS.SECTION_BASIC_DETAILS}
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={FACILITY_LABELS.FIELD_FACILITY_ID}
            value={fmt(facility?.facilityId)}
            testId="view-facility-field-facility-id"
          />
          <InfoField
            label={FACILITY_LABELS.FIELD_PHONE}
            value={formatPhone(facility?.countryCode, facility?.phoneNumber)}
            testId="view-facility-field-phone"
          />
          <InfoField
            label={FACILITY_LABELS.FIELD_NIP}
            value={formatNip(facility?.nip)}
            testId="view-facility-field-nip"
          />
          <InfoField
            label={FACILITY_LABELS.CREATED_DATE_LABEL}
            value={formatDate(facility?.createdAt)}
            testId="view-facility-field-created-at"
          />
        </div>
      </div>

      {/* Dane adresowe */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p className="text-titleDefault font-medium text-on-surface-variant">
          {FACILITY_LABELS.SECTION_ADDRESS}
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={FACILITY_LABELS.FIELD_ADDRESS1}
            value={fmt(facility?.addressLine1)}
            testId="view-facility-field-address1"
          />
          <InfoField
            label={`${FACILITY_LABELS.FIELD_ADDRESS2}`}
            value={fmt(facility?.addressLine2)}
            testId="view-facility-field-address2"
          />
          <InfoField
            label={FACILITY_LABELS.FIELD_CITY}
            value={fmt(facility?.city)}
            testId="view-facility-field-city"
          />
          <InfoField
            label={FACILITY_LABELS.FIELD_STATE}
            value={fmt(facility?.state)}
            testId="view-facility-field-state"
          />
          <InfoField
            label={FACILITY_LABELS.FIELD_ZIP}
            value={fmt(facility?.zipCode)}
            testId="view-facility-field-zip"
          />
          <InfoField
            label={FACILITY_LABELS.FIELD_COUNTRY}
            value={fmt(facility?.country)}
            testId="view-facility-field-country"
          />
        </div>
      </div>
    </div>
  );
};

export default ViewFacilityBasicInfo;
